import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import axios from 'axios';

const emptyForm = {
  id: '',
  slug: '',
  title: '',
  city: '',
  state: '',
  venueName: '',
  addressLine: '',
  meetingLink: '',
  zipCode: '',
  eventDate: '',
  startTime: '11:00',
  endTime: '12:30',
  timezone: 'America/New_York',
  topic: '',
  description: '',
  walkInAllowed: true,
  onlineAvailable: true,
  capacity: '',
  status: 'scheduled',
};

const getShareUrl = (meetup) => {
  const origin = window.location.origin;
  const key = meetup.slug || meetup.id;
  return `${origin}/2.0/community-meetup/${encodeURIComponent(key)}/register`;
};

const copyText = async (value) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  window.prompt('Copy this share link:', value);
};

const normalizeMeetupForForm = (meetup) => ({
  id: meetup.id || '',
  slug: meetup.slug || '',
  title: meetup.title || '',
  city: meetup.city || '',
  state: meetup.state || '',
  venueName: meetup.venueName || '',
  addressLine: meetup.addressLine || '',
  meetingLink: meetup.meetingLink || '',
  zipCode: meetup.zipCode || '',
  eventDate: meetup.eventDate || '',
  startTime: meetup.startTime || '',
  endTime: meetup.endTime || '',
  timezone: meetup.timezone || 'America/New_York',
  topic: meetup.topic || '',
  description: meetup.description || '',
  walkInAllowed: Boolean(meetup.walkInAllowed),
  onlineAvailable: Boolean(meetup.onlineAvailable),
  capacity: meetup.capacity || '',
  status: meetup.status || 'scheduled',
});

const CommunityMeetupsManager = () => {
  const token = localStorage.getItem('token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [meetups, setMeetups] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingReminderId, setSendingReminderId] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [lastSavedShareUrl, setLastSavedShareUrl] = useState('');

  const fetchMeetups = useCallback(() => {
    setLoading(true);
    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/api/admin/community-meetups?includeInactive=true`, { headers })
      .then((res) => setMeetups(res.data?.meetups || []))
      .catch((err) => {
        console.error('Error loading community meetups', err);
        setMessage({
          type: 'error',
          text: err?.response?.data?.message || 'Unable to load community meetups.',
        });
      })
      .finally(() => setLoading(false));
  }, [headers]);

  useEffect(() => {
    fetchMeetups();
  }, [fetchMeetups]);

  const updateForm = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const saveMeetup = (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });
    setLastSavedShareUrl('');

    const payload = {
      ...form,
      walkInAllowed: Boolean(form.walkInAllowed),
      onlineAvailable: Boolean(form.onlineAvailable),
      capacity: form.capacity === '' ? null : Number(form.capacity),
    };

    const request = form.id
      ? axios.put(`${process.env.REACT_APP_API_BASE_URL}/api/admin/community-meetups/${form.id}`, payload, { headers })
      : axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/admin/community-meetups`, payload, { headers });

    request
      .then((res) => {
        const slug = res.data?.slug || form.slug;
        const shareUrl = res.data?.registrationUrl || getShareUrl({ ...form, id: res.data?.id || form.id, slug });
        setMessage({
          type: 'success',
          text: 'Community meetup saved. Registration / share link is ready below.',
        });
        setLastSavedShareUrl(shareUrl);
        setForm(emptyForm);
        fetchMeetups();
      })
      .catch((err) => {
        console.error('Error saving community meetup', err);
        setMessage({
          type: 'error',
          text: err?.response?.data?.message || 'Unable to save community meetup.',
        });
      })
      .finally(() => setSaving(false));
  };

  const cancelMeetup = (meetup) => {
    if (!window.confirm(`Cancel "${meetup.title}"? It will stop showing publicly.`)) return;

    axios
      .delete(`${process.env.REACT_APP_API_BASE_URL}/api/admin/community-meetups/${meetup.id}`, { headers })
      .then(() => {
        setMessage({ type: 'success', text: 'Community meetup cancelled.' });
        fetchMeetups();
      })
      .catch((err) => {
        console.error('Error cancelling community meetup', err);
        setMessage({
          type: 'error',
          text: err?.response?.data?.message || 'Unable to cancel community meetup.',
        });
      });
  };

  const sendReminder = (meetup) => {
    const recipientCount = Number(meetup.registeredCount || 0);
    if (!meetup.meetingLink) {
      setMessage({
        type: 'error',
        text: 'Add and save the private Google Meet / Zoom link before sending reminders.',
      });
      return;
    }
    if (!recipientCount) {
      setMessage({ type: 'info', text: 'This meetup has no registered participants.' });
      return;
    }
    if (!window.confirm(
      `Send the updated meetup details and private meeting link to ${recipientCount} registered participant${recipientCount === 1 ? '' : 's'}?`
    )) return;

    setSendingReminderId(meetup.id);
    setMessage({ type: '', text: '' });
    axios
      .post(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/community-meetups/${meetup.id}/send-reminder`,
        { confirm: true },
        { headers }
      )
      .then((res) => {
        setMessage({
          type: res.data?.failed ? 'warning' : 'success',
          text: res.data?.message || 'Meetup reminders sent.',
        });
        fetchMeetups();
      })
      .catch((err) => {
        console.error('Error sending community meetup reminders', err);
        setMessage({
          type: 'error',
          text: err?.response?.data?.message || 'Unable to send meetup reminders.',
        });
      })
      .finally(() => setSendingReminderId(null));
  };

  const copyShareLink = async (meetup) => {
    const shareUrl = getShareUrl(meetup);
    await copyText(shareUrl);
    setMessage({ type: 'success', text: 'Share link copied.' });
  };

  const openSocialShare = (meetup, channel) => {
    const shareUrl = getShareUrl(meetup);
    const text = `${meetup.title} | UC Community Meetup`;
    const encodedUrl = encodeURIComponent(shareUrl);
    const encodedText = encodeURIComponent(text);
    const urls = {
      whatsapp: `https://wa.me/?text=${encodedText}%20${encodedUrl}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      x: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    };
    window.open(urls[channel], '_blank', 'noopener,noreferrer');
  };

  const activeMeetups = meetups.filter((meetup) => ['scheduled', 'full'].includes(meetup.status));
  const totalRegistrations = meetups.reduce(
    (sum, meetup) => sum + Number(meetup.registeredCount || 0),
    0
  );

  if (loading) return <CircularProgress />;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 900 }}>
            UC Community Meetups
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Add city-center, library, and online public sessions. Each meetup gets a social share link.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Chip label={`${meetups.length} total`} color="primary" variant="outlined" />
          <Chip label={`${activeMeetups.length} public`} color="success" variant="outlined" />
          <Chip label={`${totalRegistrations} RSVPs`} color="warning" variant="outlined" />
        </Stack>
      </Box>

      {message.text && (
        <Alert severity={message.type || 'info'} sx={{ mb: 2 }}>
          {message.text}
        </Alert>
      )}

      {lastSavedShareUrl && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 2,
            border: '1px solid #bfdbfe',
            borderRadius: 3,
            bgcolor: '#eff6ff',
          }}
        >
          <Typography sx={{ fontWeight: 900, color: '#0f172a', mb: 0.5 }}>
            Registration / share link
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: '#334155', wordBreak: 'break-all', mb: 1.5 }}
          >
            {lastSavedShareUrl}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button
              variant="contained"
              size="small"
              onClick={async () => {
                await copyText(lastSavedShareUrl);
                setMessage({ type: 'success', text: 'Share link copied.' });
              }}
            >
              Copy link
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => window.open(lastSavedShareUrl, '_blank', 'noopener,noreferrer')}
            >
              Open registration page
            </Button>
          </Stack>
        </Paper>
      )}

      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          border: '1px solid #dbeafe',
          borderRadius: 3,
          bgcolor: '#f8fbff',
        }}
      >
        <Typography sx={{ fontWeight: 900, color: '#0f172a', mb: 1 }}>
          {form.id ? 'Edit meetup' : 'Add new meetup'}
        </Typography>
        <Box component="form" onSubmit={saveMeetup} sx={{ display: 'grid', gap: 2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.3fr 0.7fr 0.5fr' }, gap: 2 }}>
            <TextField label="Title" value={form.title} onChange={(e) => updateForm('title', e.target.value)} required size="small" />
            <TextField label="City" value={form.city} onChange={(e) => updateForm('city', e.target.value)} required size="small" />
            <TextField label="State" value={form.state} onChange={(e) => updateForm('state', e.target.value)} size="small" />
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 2 }}>
            <TextField label="Event date" type="date" value={form.eventDate} onChange={(e) => updateForm('eventDate', e.target.value)} required size="small" InputLabelProps={{ shrink: true }} />
            <TextField label="Start time" type="time" value={form.startTime} onChange={(e) => updateForm('startTime', e.target.value)} size="small" InputLabelProps={{ shrink: true }} />
            <TextField label="End time" type="time" value={form.endTime} onChange={(e) => updateForm('endTime', e.target.value)} size="small" InputLabelProps={{ shrink: true }} />
            <TextField label="Capacity" type="number" value={form.capacity} onChange={(e) => updateForm('capacity', e.target.value)} size="small" />
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            <TextField label="Venue name" value={form.venueName} onChange={(e) => updateForm('venueName', e.target.value)} size="small" />
            <TextField label="Address / meeting note" value={form.addressLine} onChange={(e) => updateForm('addressLine', e.target.value)} size="small" />
            <TextField
              label="Private Google Meet / Zoom link"
              type="url"
              value={form.meetingLink}
              onChange={(e) => updateForm('meetingLink', e.target.value)}
              size="small"
              helperText="Admin-only. Included in confirmation and reminder emails; never shown on the public page."
            />
            <TextField label="ZIP code" value={form.zipCode} onChange={(e) => updateForm('zipCode', e.target.value)} size="small" />
            <TextField label="Timezone" value={form.timezone} onChange={(e) => updateForm('timezone', e.target.value)} size="small" />
          </Box>

          <TextField label="Topic" value={form.topic} onChange={(e) => updateForm('topic', e.target.value)} size="small" />
          <TextField label="Description" value={form.description} onChange={(e) => updateForm('description', e.target.value)} multiline minRows={3} />

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 2 }}>
            <TextField label="Walk-ins" select SelectProps={{ native: true }} value={String(form.walkInAllowed)} onChange={(e) => updateForm('walkInAllowed', e.target.value === 'true')} size="small">
              <option value="true">Allowed</option>
              <option value="false">Registration only</option>
            </TextField>
            <TextField label="Online option" select SelectProps={{ native: true }} value={String(form.onlineAvailable)} onChange={(e) => updateForm('onlineAvailable', e.target.value === 'true')} size="small">
              <option value="true">Available</option>
              <option value="false">Not available</option>
            </TextField>
            <TextField label="Status" select SelectProps={{ native: true }} value={form.status} onChange={(e) => updateForm('status', e.target.value)} size="small">
              <option value="scheduled">Scheduled</option>
              <option value="full">Full</option>
              <option value="cancelled">Cancelled</option>
              <option value="completed">Completed</option>
            </TextField>
            <TextField label="Share slug (optional)" value={form.slug} onChange={(e) => updateForm('slug', e.target.value)} size="small" helperText="Leave blank to auto-generate." />
          </Box>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button type="submit" variant="contained" disabled={saving}>
              {saving ? 'Saving…' : form.id ? 'Update Meetup' : 'Add Meetup'}
            </Button>
            {form.id && (
              <Button
                variant="outlined"
                onClick={() => {
                  setForm(emptyForm);
                  setLastSavedShareUrl('');
                }}
              >
                Cancel Edit
              </Button>
            )}
            <Button variant="text" onClick={fetchMeetups}>
              Refresh
            </Button>
          </Stack>
        </Box>
      </Paper>

      <Box sx={{ display: 'grid', gap: 1.5 }}>
        {meetups.map((meetup) => {
          const shareUrl = getShareUrl(meetup);
          return (
            <Paper
              key={meetup.id}
              elevation={0}
              sx={{
                p: 2,
                border: '1px solid #e2e8f0',
                borderRadius: 3,
                bgcolor: meetup.status === 'cancelled' ? '#f8fafc' : '#ffffff',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                <Box>
                  <Typography sx={{ fontWeight: 900, color: '#0f172a' }}>
                    {meetup.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {meetup.eventDate} · {meetup.startTime || 'TBA'} – {meetup.endTime || 'TBA'} · {meetup.city}{meetup.state ? `, ${meetup.state}` : ''}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#475569', mt: 0.5 }}>
                    {meetup.topic}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
                  <Chip label={meetup.status} size="small" color={meetup.status === 'scheduled' ? 'success' : 'default'} />
                  <Chip label={`${meetup.registeredCount || 0}/${meetup.capacity || '∞'} RSVPs`} size="small" variant="outlined" />
                  <Chip
                    label={meetup.meetingLink ? 'Private meeting link ready' : 'Meeting link missing'}
                    size="small"
                    color={meetup.meetingLink ? 'success' : 'warning'}
                    variant="outlined"
                  />
                </Stack>
              </Box>

              <Divider sx={{ my: 1.5 }} />

              <Typography variant="caption" sx={{ display: 'block', color: '#64748b', mb: 1 }}>
                Registration link: {shareUrl}
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setForm(normalizeMeetupForForm(meetup));
                    setLastSavedShareUrl('');
                  }}
                >
                  Edit
                </Button>
                <Button size="small" color="error" variant="outlined" onClick={() => cancelMeetup(meetup)} disabled={meetup.status === 'cancelled'}>
                  Cancel Meetup
                </Button>
                <Button size="small" variant="outlined" onClick={() => copyShareLink(meetup)}>
                  Copy Link
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  onClick={() => sendReminder(meetup)}
                  disabled={
                    sendingReminderId === meetup.id ||
                    !meetup.meetingLink ||
                    !Number(meetup.registeredCount || 0) ||
                    !['scheduled', 'full'].includes(meetup.status)
                  }
                >
                  {sendingReminderId === meetup.id
                    ? 'Sending…'
                    : `Send Reminder (${meetup.registeredCount || 0})`}
                </Button>
                <Button size="small" variant="text" onClick={() => openSocialShare(meetup, 'whatsapp')}>
                  WhatsApp
                </Button>
                <Button size="small" variant="text" onClick={() => openSocialShare(meetup, 'facebook')}>
                  Facebook
                </Button>
                <Button size="small" variant="text" onClick={() => openSocialShare(meetup, 'linkedin')}>
                  LinkedIn
                </Button>
                <Button size="small" variant="text" onClick={() => openSocialShare(meetup, 'x')}>
                  X
                </Button>
              </Stack>
            </Paper>
          );
        })}
      </Box>
    </Box>
  );
};

export default CommunityMeetupsManager;
